import { useState, useEffect } from 'react';
import type Parking from '../models/parking';
import {
  getAllParkingsForAuthorEmail,
  getAllParkingsForOffice,
  getAllParkingsForOfficeOnDate,
  getParkingById,
} from '../services/parkings.service';
import type AgendaEntryParking from '../../check-in-employees/models/agenda-entry-parking';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { dataCache, PARKINGS_CACHE_KEY } from '../../../core/utils/data-cache';

interface InitialParkingState {
  readonly initialized: boolean;
  readonly error?: boolean;
}

interface ParkingsState extends InitialParkingState {
  readonly parkings?: Parking[];
}

interface ParkingState extends InitialParkingState {
  readonly parking?: Parking;
}

interface ParkingsAgendaEntryState extends InitialParkingState {
  readonly agendaEntryParkings?: AgendaEntryParking[];
}

interface Props {
  readonly id: string;
}

export const useParkingsForOffice = ({ id }: Props): ParkingsState => {
  const [state, setState] = useState<ParkingsState>({
    initialized: false,
  });
  const keycloak = useKeycloak();

  useEffect(() => {
    const getParkings = async (): Promise<void> => {
      try {
        const parkings = (await getAllParkingsForOffice(keycloak, id)) ?? [];
        setState({ initialized: true, parkings });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    if (!state.initialized) {
      getParkings();
    }
  }, [setState, state.initialized, keycloak, id]);

  return state;
};

/**
 * Returns all parkings that the current user owns or has access to via an
 * associated office.
 *
 * Performance optimisation: the response is cached in memory so that repeated
 * visits to "My Offices" render the parking list instantly.  A background fetch
 * always runs on mount to keep the cache up-to-date.  The cache is invalidated
 * by the service layer whenever a parking is created, updated, or deleted.
 *
 * @param version - Increment this value to force an explicit cache flush and
 *   re-fetch (e.g. when the admin presses "Refresh data").  Defaults to 0.
 */
export const useParkings = (version = 0): ParkingsState => {
  // Serve cached data immediately so the spinner is skipped on revisit.
  const [state, setState] = useState<ParkingsState>(() => {
    const cached = dataCache.get<Parking[]>(PARKINGS_CACHE_KEY);
    return cached != null
      ? { initialized: true, parkings: cached }
      : { initialized: false };
  });

  const keycloak = useKeycloak();

  useEffect(() => {
    let cancelled = false;

    // When an explicit refresh is requested (version > 0), show loading state.
    if (version > 0) {
      setState({ initialized: false });
    }

    const getParkings = async (): Promise<void> => {
      try {
        const parkings = (await getAllParkingsForAuthorEmail(keycloak)) ?? [];
        if (!cancelled) {
          dataCache.set(PARKINGS_CACHE_KEY, parkings);
          setState({ initialized: true, parkings });
        }
      } catch (error) {
        if (!cancelled && !dataCache.get(PARKINGS_CACHE_KEY)) {
          setState({ initialized: false, error: true });
        }
      }
    };

    // Always revalidate in the background.
    getParkings();

    return (): void => {
      cancelled = true;
    };
  }, [keycloak, version]);

  return state;
};

export const useParking = ({ id }: Props): ParkingState => {
  const [state, setState] = useState<ParkingState>({
    initialized: false,
  });
  const keycloak = useKeycloak();

  useEffect(() => {
    const getParking = async (): Promise<void> => {
      try {
        const parking = await getParkingById(id, keycloak);
        setState({ initialized: true, parking });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    if (!state.initialized) {
      getParking();
    }
  }, [setState, state.initialized, id, keycloak]);

  return state;
};

interface OfficeProps {
  officeId: string | undefined;
  date: string;
}

export const useParkingsForOfficeOnDate = ({ officeId, date }: OfficeProps): ParkingsAgendaEntryState => {
  const [state, setState] = useState<ParkingsAgendaEntryState>({
    initialized: false,
  });
  const keycloak = useKeycloak();

  useEffect(() => {
    const getParkings = async (): Promise<void> => {
      if (!officeId) {
        return;
      }
      try {
        const agendaEntryParkings = await getAllParkingsForOfficeOnDate(keycloak, officeId, date);
        setState({ initialized: true, agendaEntryParkings });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };
    getParkings();
  }, [setState, keycloak, officeId, date]);

  return state;
};
